import { useMutation, useQueryClient } from "@tanstack/react-query";
import i18next from "i18next";
import { ApiCustodyDeposit } from "@/api";
import {
  CustodyDepositResultCode,
  type CustodyDepositRequest,
  type CustodyDepositResponse,
} from "@/api/types";
import { getTokenName } from "@/config/common";
import { USDT_ADDRESS } from "@/config/env";
import { showError } from "@/lib/toast";

/** 与 {@link CustodyDepositResultCode} 一一对应的 i18n key（`toast.custody.deposit.result.*`） */
export const CUSTODY_DEPOSIT_RESULT_I18N_KEY: Record<
  Exclude<CustodyDepositResultCode, CustodyDepositResultCode.Success>,
  string
> = {
  [CustodyDepositResultCode.AmountInvalid]:
    "toast.custody.deposit.result.amountInvalid",
  [CustodyDepositResultCode.BelowMin]: "toast.custody.deposit.result.belowMin",
  [CustodyDepositResultCode.AgreementNotSigned]:
    "toast.custody.deposit.result.agreementNotSigned",
  [CustodyDepositResultCode.ExceedMaxPerUser]:
    "toast.custody.deposit.result.exceedMaxPerUser",
  [CustodyDepositResultCode.InsufficientUsdt]:
    "toast.custody.deposit.result.insufficientUsdt",
  [CustodyDepositResultCode.OrderStatusInvalid]:
    "toast.custody.deposit.result.orderStatusInvalid",
  [CustodyDepositResultCode.UtcDayMismatch]:
    "toast.custody.deposit.result.utcDayMismatch",
  [CustodyDepositResultCode.ExceedMonthlyQuota]:
    "toast.custody.deposit.result.exceedMonthlyQuota",
  [CustodyDepositResultCode.Busy]: "toast.custody.deposit.result.busy",
  [CustodyDepositResultCode.Exception]:
    "toast.custody.deposit.result.exception",
};

export function mapCustodyDepositResultCodeToI18nKey(
  resultCode: number
): string {
  const key =
    CUSTODY_DEPOSIT_RESULT_I18N_KEY[
      resultCode as keyof typeof CUSTODY_DEPOSIT_RESULT_I18N_KEY
    ];
  return key ?? "toast.custody.deposit.result.unknown";
}

export function useCustodyDeposit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: CustodyDepositRequest
    ): Promise<CustodyDepositResponse> => {
      const res = await ApiCustodyDeposit(data);
      console.log("ApiCustodyDeposit", res);
      return res.data;
    },
    onSuccess: data => {
      const code = Number(data.resultCode);
      if (code === CustodyDepositResultCode.Success) {
        queryClient.invalidateQueries({ queryKey: ["custodyOrders"] });
        queryClient.invalidateQueries({ queryKey: ["userCustodyInfo"] });
        queryClient.invalidateQueries({ queryKey: ["balanceV2"] });
        return;
      }
      showError(
        i18next.t(mapCustodyDepositResultCodeToI18nKey(code), {
          tokenNameUsdt: getTokenName(USDT_ADDRESS),
        })
      );
    },
  });
}
